import { ReactNode } from "react";
import { SlidersHorizontal } from "lucide-react";
import t from "@/lang/lang";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { useHasFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";
import { cn } from "@/shared/lib/utils";

/**
 * MicrobialAssessmentDetailsDialog edits the details of a microbial
 * assessment that rarely change and would crowd the samples table: the
 * recommendation, and whatever the row of a sample kind passes as
 * children, e.g. its reference values. Edits are saved as they are typed.
 *
 * The trigger is highlighted while a field of the dialog has an error, so
 * an error is visible without opening the dialog. fieldNames are the keys
 * of the fields the children edit; the recommendation is always included.
 */
export function MicrobialAssessmentDetailsDialog({
  recommendation,
  onRecommendationChange,
  recommendationFieldName,
  fieldNames = [],
  disabled,
  children,
}: {
  recommendation: string;
  onRecommendationChange: (recommendation: string) => void;
  recommendationFieldName: string;
  fieldNames?: string[];
  disabled?: boolean;
  children?: ReactNode;
}) {
  const { hasErrors } = useHasFieldErrors([
    recommendationFieldName,
    ...fieldNames,
  ]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={t("Details")}
          className={cn(hasErrors && "text-destructive")}
        >
          <SlidersHorizontal />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Details")}</DialogTitle>
          <DialogDescription>
            {t(
              "Die Handlungsempfehlung wird aus dem Ergebnis abgeleitet und kann angepasst werden. Ändert sich das Ergebnis, wird sie neu abgeleitet.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TextareaField
            label={t("Handlungsempfehlung")}
            value={recommendation}
            onChange={(e) => onRecommendationChange(e.target.value)}
            fieldName={recommendationFieldName}
            disabled={disabled}
          />
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
